import React from 'react';
import './Content.css';

// Helper to escape regex characters
const escapeRegExp = (string) => {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
};

// Helper component to highlight text and bold quotes
const HighlightAndQuotes = ({ text, highlight }) => {
  // First, handle the bold quotes requirement
  // We look for text enclosed in double quotes "..." or Khmer quotes “...”
  // Using a regex with capturing group to include the quotes in the split result
  // Added support for standard quotes "..." and also consistent Khmer quotes if used
  const quoteRegex = /([“"].*?[”"])/g;
  
  // If the text has no quotes, we just do the highlighting
  if (!text.match(quoteRegex)) {
      return <HighlightText text={text} highlight={highlight} />;
  }

  const parts = text.split(quoteRegex).map((part, index) => {
    // With split(regexWithCaptureGroup), the odd indices are the captured separators (the quoted text)
    if (index % 2 === 1) {
        return <strong key={index}><HighlightText text={part} highlight={highlight} /></strong>;
    }
    // Even indices are the text between quotes
    return <HighlightText key={index} text={part} highlight={highlight} />;
  });

  return <>{parts}</>;
};

// Internal helper for just the highlighting logic (extracted from original HighlightAndQuotes)
const HighlightText = ({ text, highlight }) => {
  if (!highlight || highlight.trim() === '') {
    return <>{text}</>;
  }

  // Remove zero-width spaces from both text and highlight
  const cleanText = text.replace(/\u200B/g, '');
  const cleanHighlight = highlight.replace(/\u200B/g, '');

  // Split valid keywords by comma
  const keywords = cleanHighlight.split(',').map(k => k.trim()).filter(k => k);

  if (keywords.length === 0) {
    return <>{cleanText}</>;
  }

  // Function to create a regex string for a single term
  const createPatternString = (term) => {
    // 1. Normalize the search term: remove spaces between មាត្រា and digits
    const normalizedTerm = term.replace(/មាត្រា\s+([0-9\u17E0-\u17E9])/gi, 'មាត្រា$1');
    
    // 2. Escape special characters
    let escaped = escapeRegExp(normalizedTerm);
    
    // 3. Inject optional whitespace matching into the regex pattern
    return escaped.replace(/មាត្រា([0-9\u17E0-\u17E9])/gi, 'មាត្រា\\s*$1');
  };

  // Combine all keywords into one large OR pattern: (term1|term2|term3)
  const patternString = keywords.map(createPatternString).join('|');
  const regex = new RegExp(`(${patternString})`, 'gi');

  const parts = cleanText.split(regex).map((part, index) => {
    // Because we wrapped the pattern in capture group (), split will return:
    // [textBefore, match, textAfter, match, ...]
    // So odd indices (1, 3, 5...) are the matches.
    if (index % 2 === 1) {
      return <span key={index} style={{ backgroundColor: 'yellow' }}>{part}</span>;
    }
    return part;
  });

  return <>{parts}</>;
}

// Helper to convert numbers to Roman numerals
const toRomanNumeral = (num) => {
  const romanNumerals = [
    '', 'i', 'ii', 'iii', 'iv', 'v', 'vi', 'vii', 'viii', 'ix', 'x',
    'xi', 'xii', 'xiii', 'xiv', 'xv', 'xvi', 'xvii', 'xviii', 'xix', 'xx'
  ];
  return romanNumerals[num] || num.toString();
};

// Helper component to format English text with markers
const EnglishFormatter = ({ text, highlight }) => {
  // Regex patterns for English markers - ordered longest to shortest for proper matching
  const romanMarkerRegex = /^(\s*)(xv|xiv|xiii|xii|xi|x|ix|viii|vii|vi|v|iv|iii|ii|i)(\.\s+)/i;
  const letterMarkerRegex = /^(\s*)([a-z])(\.\s+)/i;
  
  const trimmedText = text.trim();
  const romanMatch = trimmedText.match(romanMarkerRegex);
  const letterMatch = trimmedText.match(letterMarkerRegex);
  
  if (romanMatch) {
    const marker = romanMatch[2] + romanMatch[3];
    const restOfText = trimmedText.substring(marker.length);
    return (
      <>
        <strong>{marker}</strong>
        <HighlightAndQuotes text={restOfText} highlight={highlight} />
      </>
    );
  }
  
  if (letterMatch) {
    const marker = letterMatch[2] + letterMatch[3];
    const restOfText = trimmedText.substring(marker.length);
    return (
      <>
        <strong>{marker}</strong>
        <HighlightAndQuotes text={restOfText} highlight={highlight} />
      </>
    );
  }
  
  return <HighlightAndQuotes text={text} highlight={highlight} />;
};

// Helper component to format special words
const FinalFormatter = ({ text, highlight }) => {
  const specialWords = [
    "(វិសោធនកម្ម)",
    "(កំណត់)",
    "(កំណត់បញ្ចប់)",
    "(កំណត់ពន្យល់បន្ថែម)",
    "(និរាករណ៍)",
    "(ត្រូវលុបចោល)",
    "(មាត្រាពាក់ព័ន្ធ)"
  ];

  // Find the first special word that appears in the text
  let foundWord = null;
  let foundIndex = -1;
  for (const word of specialWords) {
    const idx = text.indexOf(word);
    if (idx !== -1 && (foundIndex === -1 || idx < foundIndex)) {
      foundWord = word;
      foundIndex = idx;
    }
  }

  const trimmedText = text.trim();
  const discussionCodeRegex = /(ក្រមពិភាក្សារដ្ឋប្បវេណី\s*\([^)]*\))/;
  const articleLineRegex = /^Article\s+\d+/i;

  const discussionMatch = text.match(discussionCodeRegex);
  if (discussionMatch) {
    const parts = text.split(discussionCodeRegex);
    return (
      <>
        {parts.map((part, index) => (
          index % 2 === 1
            ? <strong key={index}><HighlightAndQuotes text={part} highlight={highlight} /></strong>
            : <HighlightAndQuotes key={index} text={part} highlight={highlight} />
        ))}
      </>
    );
  }

  if (articleLineRegex.test(trimmedText)) {
    return (
      <strong>
        <HighlightAndQuotes text={text} highlight={highlight} />
      </strong>
    );
  }

  if (foundWord && foundIndex !== -1) {
    const beforeText = text.substring(0, foundIndex);
    const wordItself = foundWord;
    const afterText = text.substring(foundIndex + foundWord.length);

    // If the special word is one of the indent markers, indent every resulting line after it by 1cm
    if (['(កំណត់)', '(កំណត់បញ្ចប់)', '(មាត្រាពាក់ព័ន្ធ)'].includes(wordItself)) {
      const lines = afterText.split('\n');
      return (
        <>
          {beforeText && beforeText.trim() !== '' ? (
            <>
              <HighlightAndQuotes text={beforeText} highlight={highlight} />
            </>
          ) : null}
          <strong style={{ color: 'orange', display: 'block', marginTop: '0', marginBottom: '0' }}>{wordItself}</strong>
          {afterText && afterText.trim() !== '' ? (
            <>
              <div style={{ color: 'orange', marginTop: '0', paddingTop: '0' }}>
                {lines.map((line, index) => (
                  <div
                    key={index}
                    style={{
                      textIndent: '1cm',
                      whiteSpace: 'pre-wrap',
                      margin: 0,
                      padding: 0,
                      display: 'block'
                    }}
                  >
                    {line === '' ? <>&nbsp;</> : <HighlightAndQuotes text={line} highlight={highlight} />}
                  </div>
                ))}
              </div>
            </>
          ) : null}
        </>
      );
    }

    return (
      <>
        {beforeText && beforeText.trim() !== '' ? (
          <>
            <HighlightAndQuotes text={beforeText} highlight={highlight} />
          </>
        ) : null}
        <strong style={{ color: 'orange', display: 'block', marginTop: '0', marginBottom: '0' }}>{wordItself}</strong>
        {afterText && afterText.trim() !== '' ? (
          <>
            <span style={{ color: 'orange', display: 'block', marginTop: '0', paddingTop: '0' }}>
              <HighlightAndQuotes text={afterText} highlight={highlight} />
            </span>
          </>
        ) : null}
      </>
    );
  }

  // If no special word is found, just apply the standard formatting.
  return <HighlightAndQuotes text={text} highlight={highlight} />;
};


// A component to render a single search result item
// The `style` prop is no longer needed from react-window
const ResultItem = ({ result, searchQuery, backgroundColor, showPdfLink, pdfPath, onPdfClick }) => {
  const { name, source } = result;

  const normalizeMarkerLineBreaks = (text) => {
    return text
      .replace(/\r\n/g, '\n')
      .replace(/[ \t]+\n/g, '\n')
      .replace(
        /\n*[ \t]*(\((?:វិសោធនកម្ម|កំណត់|កំណត់បញ្ចប់|កំណត់ពន្យល់បន្ថែម|និរាករណ៍|ត្រូវលុបចោល|មាត្រាពាក់ព័ន្ធ)\))[ \t]*\n*/g,
        '\n\n$1\n'
      )
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  };

  // Remove all '<' characters from the content and ensure markers start a new line
  const cleanSegment = normalizeMarkerLineBreaks(name.replace(/</g, ''));

  let firstLine = cleanSegment;
  let restOfContent = '';
  const lineBreakIndex = cleanSegment.indexOf('\n');
  if (lineBreakIndex !== -1) {
    firstLine = cleanSegment.substring(0, lineBreakIndex);
    restOfContent = cleanSegment.substring(lineBreakIndex + 1);
  }

  const cardStyle = {
    backgroundColor: backgroundColor,
    padding: '20px',
    borderRadius: '12px',
    boxShadow: '0 10px 20px rgba(0,0,0,0.1), 0 3px 6px rgba(0,0,0,0.1)',
    border: '1px solid #e2e8f0',
    maxWidth: '1350px', // Re-add maxWidth for a single column layout
    margin: '0 auto 1.5em auto', // Center the card and add bottom margin
    boxSizing: 'border-box'
  };

  return (
    // No outer div wrapper needed anymore
    <div className="file-result-item" style={cardStyle}>
      <p style={{ marginBottom: '5px', fontWeight: 'bold' }}>
        <FinalFormatter text={firstLine} highlight={searchQuery} />
      </p>
      {restOfContent && (
        <div>
          {(() => {
            const lines = restOfContent.split(/\r?\n/);
            const elements = [];
            let i = 0;
            let indentAfterKdam = false;
            let parentMarkerType = null;
            const specialWords = [
              '(វិសោធនកម្ម)',
              '(កំណត់)',
              '(កំណត់បញ្ចប់)',
              '(កំណត់ពន្យល់បន្ថែម)',
              '(និរាករណ៍)',
              '(ត្រូវលុបចោល)',
              '(មាត្រាពាក់ព័ន្ធ)'
            ];

            const lineStyle = (indented) => ({
              margin: 0,
              paddingLeft: '20px',
              textIndent: indented ? '1cm' : 0,
              whiteSpace: 'pre-wrap',
              tabSize: 6,
              overflowX: 'auto'
            });

            while (i < lines.length) {
              const line = lines[i];
              const trimmedLine = line.trim();

              const isBlank = trimmedLine === '';
              const indentMarkers = ['(កំណត់)', '(កំណត់បញ្ចប់)', '(មាត្រាពាក់ព័ន្ធ)'];
              const isIndentMarkerLine = indentMarkers.some((marker) => line.includes(marker));
              const isOtherSpecialLine = specialWords.some((word) => !indentMarkers.includes(word) && line.includes(word));
              if (isBlank) {
                elements.push(
                  <p key={i} style={lineStyle(indentAfterKdam)}>
                    &nbsp;
                  </p>
                );
                i++;
                continue;
              }

              if (isIndentMarkerLine) {
                indentAfterKdam = true;
              } else if (indentAfterKdam && isOtherSpecialLine) {
                indentAfterKdam = false;
              }

              const numberMarkerRegex = /^\s*([០-៩]+\s*-)/;
              const letterMarkerRegex = /^\s*([ក-អ]\s*-)/;
              const hyphenMarkerRegex = /^\s*(-)/;
              const markerRegex = /^\s*((?:\d+|[ivxlcdm]+|[a-z])(?:\.(?:\d+|[ivxlcdm]+|[a-z]))*)\.\s*/i;
              const parenNumberMarkerRegex = /^\s*\((\d+|[១២៣៤៥៦៧៨៩០]+)\)\s*/;
              const markerMatch = trimmedLine.match(markerRegex);
              const parenNumberMatch = trimmedLine.match(parenNumberMarkerRegex);
              const numberMatch = trimmedLine.match(numberMarkerRegex);
              const letterMatch = trimmedLine.match(letterMarkerRegex);
              const hyphenMatch = trimmedLine.match(hyphenMarkerRegex);

              if (indentAfterKdam && parenNumberMatch) {
                const marker = `(${parenNumberMatch[1]})`;
                const restOfLine = trimmedLine.substring(parenNumberMatch[0].length);
                elements.push(
                  <p key={i} style={{ ...lineStyle(indentAfterKdam), paddingLeft: '20px' }}>
                    <strong>{marker}</strong>{' '}
                    <HighlightAndQuotes text={restOfLine} highlight={searchQuery} />
                  </p>
                );
                parentMarkerType = 'paren';
              } else if (markerMatch) {
                const markerKey = markerMatch[1];
                const marker = `${markerKey}.`;
                const restOfLine = trimmedLine.substring(markerMatch[0].length);
                const isSimpleRoman = /^[ivxlcdm]+$/i.test(markerKey);
                const isSimpleLetter = /^[a-z]$/i.test(markerKey);
                const isNumberMarker = /^\d+$/.test(markerKey);
                const isNestedNumber = markerKey.includes('.') && !isSimpleRoman && !isSimpleLetter;
                const indent = isNestedNumber || ((isSimpleRoman || isSimpleLetter) && ['number', 'roman', 'letter'].includes(parentMarkerType)) ? '60px' : '20px';
                elements.push(
                  <p key={i} style={{ ...lineStyle(indentAfterKdam), paddingLeft: indent }}>
                    <strong>{marker}</strong>{' '}
                    <HighlightAndQuotes text={restOfLine} highlight={searchQuery} />
                  </p>
                );
                if (isNumberMarker) {
                  parentMarkerType = 'number';
                } else if (isSimpleRoman) {
                  parentMarkerType = 'roman';
                } else if (isSimpleLetter) {
                  parentMarkerType = 'letter';
                } else {
                  parentMarkerType = 'dot';
                }
              } else if (letterMatch || hyphenMatch || numberMatch) {
                let indent = '20px';
                let markerPart = '';
                let restOfLine = trimmedLine;

                if (letterMatch) {
                  indent = '40px';
                  markerPart = letterMatch[1];
                  restOfLine = trimmedLine.substring(markerPart.length);
                } else if (hyphenMatch) {
                  indent = '40px';
                  markerPart = hyphenMatch[1];
                  restOfLine = trimmedLine.substring(markerPart.length);
                } else if (numberMatch) {
                  indent = '20px';
                  markerPart = numberMatch[1];
                  restOfLine = trimmedLine.substring(markerPart.length);
                }

                elements.push(
                  <p key={i} style={{ ...lineStyle(indentAfterKdam), paddingLeft: indent }}>
                    <strong>
                      <FinalFormatter text={markerPart} highlight={searchQuery} />
                    </strong>
                    <FinalFormatter text={restOfLine} highlight={searchQuery} />
                  </p>
                );
              } else {
                elements.push(
                  <p key={i} style={lineStyle(indentAfterKdam)}>
                    <FinalFormatter text={line} highlight={searchQuery} />
                  </p>
                );
              }
              i++;
            }
            return elements;
          })()}
        </div>
      )}
      <div style={{ fontSize: '0.8em', color: 'grey', marginTop: '4px', textAlign: 'right', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '10px' }}>
        {showPdfLink && (
           <button 
             onClick={() => onPdfClick(`${process.env.PUBLIC_URL}${pdfPath}${source.replace('.txt', '.pdf')}`)}
             style={{
               background: 'none',
               border: 'none',
               cursor: 'pointer',
               color: '#b8860b', /* Dark Goldenrod - readable yellow/gold */
               textDecoration: 'none',
               fontWeight: 'bold',
               display: 'flex',
               alignItems: 'center',
               gap: '5px',
               padding: 0,
               fontSize: 'inherit',
               fontFamily: 'inherit'
             }}
           >
             <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
               <path d="M7 18H17V16H7V18Z" fill="currentColor" />
               <path d="M17 14H7V12H17V14Z" fill="currentColor" />
               <path d="M7 10H11V8H7V10Z" fill="currentColor" />
               <path fillRule="evenodd" clipRule="evenodd" d="M6 2C4.34315 2 3 3.34315 3 5V19C3 20.6569 4.34315 22 6 22H18C19.6569 22 21 20.6569 21 19V9C21 5.13401 17.866 2 14 2H6ZM6 4H13V9H19V19C19 19.5523 18.5523 20 18 20H6C5.44772 20 5 19.5523 5 19V5C5 4.44772 5.44772 4 6 4ZM15 4.10002C16.6113 4.4271 17.9413 5.52906 18.584 7H15V4.10002Z" fill="currentColor" />
             </svg>
             ពិនិត្យឯកសារដើម
           </button>
        )}
        {/* Only show source text if NOT showing PDF link. If showPdfLink is true, we hide "Source: ..." */ }
        {!showPdfLink && (
           <span>Source: {source.replace('.txt', '')}</span>
        )}
      </div>
    </div>
  );
};

// The main Content component that displays all results
function Content({ fileResults, searchAttempted, isLoading, searchQuery, showPdfLink = false, pdfPath = '/' }) {
  // Determine if selection/copy should be blocked
  const blockCopy = searchQuery === "មាត្រា";
  const [selectedPdfUrl, setSelectedPdfUrl] = React.useState(null);

  // Handler to block copy event
  const handleCopy = (e) => {
    if (blockCopy) {
      e.preventDefault();
      alert('Copying is disabled for this keyword.');
    }
  };

  const handlePdfClick = (url) => {
    setSelectedPdfUrl(url);
  };

  const closePdfPopup = () => {
    setSelectedPdfUrl(null);
  };

  if (isLoading) {
    return <main style={{ marginTop: '0', padding: '20px', textAlign: 'center' }}>Loading results...</main>;
  }

  const hasFileResults = fileResults && fileResults.length > 0;

  let contentToDisplay;
  if (!searchAttempted) {
    contentToDisplay = (
      <div className="content-helper-wrapper">
        <p style={{ fontSize: '1em' }}>សូមបញ្ចូលពាក្យគន្លឹះក្នុងប្រអប់ស្វែងរកខាងលើ ដើម្បីស្វែងរកមាត្រាពាក់ព័ន្ធនឹងពាក្យគន្លឹះនោះ ។</p>
        <p style={{ fontSize: '1em' }}>លោកអ្នកអាចស្វែងរកពាក្យគន្លឹះបានច្រើនពាក្យក្នុងពេលតែមួយ ដោយប្រើសញ្ញាក្បៀស (,) ។</p>
        <p style={{ fontSize: '1em' }}> <b>ឧទាហរណ៍៖ </b> មាត្រា១២, មាត្រា ១០, រំលាយ, លុបចោល</p>

        <div className="content-helper-box">
          <p style={{ fontSize: '1em', margin: '0 0 12px 0' }}><b style={{ color: '#fbc02d' }}> ⚠ ខ បដិសេធទទួលខុសត្រូវ (Disclaimer) </b></p>
          <p style={{ fontSize: '1em', margin: '8px 0', lineHeight: '1.5' }}> អត្ថបទច្បាប់ដែលបានបង្ហាញនៅលើគេហទំព័រនេះ អាចនឹងមានកំហុសអក្ខរាវិរុទ្ធដោយអចេតនា ។  </p>
          <p style={{ fontSize: '1em', margin: '8px 0', lineHeight: '1.5' }}>សូមលោក អ្នកធ្វើការផ្ទៀងផ្ទាត់ជាមួយច្បាប់ដើមនៃច្បាប់នោះ មុននឹងយកទៅប្រើប្រាស់ជាផ្លូវការ ។  </p>
        </div>
      </div>
    );
  } else if (!hasFileResults) {
    contentToDisplay = (
      <div style={{ textAlign: 'center', marginTop: '20px' }}>
        <p>រកមិនឃើញលទ្ធផលទេ។ (No matches found for your search.)</p>
        <div style={{ fontSize: '0.8em', color: '#888', marginTop: '10px' }}>
           Debug Info: 
           Loaded Files: {fileResults ? 'Yes' : 'No'} | 
           Query: "{searchQuery}"
        </div>
      </div>
    );
  } else {
    const colors = ['#ffffff', '#dfe7f2'];
    const sourceColorMap = {};
    const uniqueSources = [...new Set(fileResults.map(r => r.source))];

    uniqueSources.forEach((source, index) => {
      sourceColorMap[source] = colors[index % 2];
    });

    contentToDisplay = (
      // Use a new class for the list container
      <section
        className="results file-results-section results-list-container"
        style={blockCopy ? { userSelect: 'none', pointerEvents: 'auto' } : {}}
        onCopy={handleCopy}
      >
        {/* Revert back to a simple .map() over the results */}
        {fileResults.map((fileResult, index) => (
          <ResultItem 
            key={fileResult.id || index} 
            result={fileResult} 
            searchQuery={searchQuery}
            backgroundColor={sourceColorMap[fileResult.source]}
            showPdfLink={showPdfLink}
            pdfPath={pdfPath}
            onPdfClick={handlePdfClick}
          />
        ))}
      </section>
    );

  }

  return (
    // Re-enable vertical scrolling on the main content area
    <main className="main-content" style={{ marginTop: '0', padding: '10px', overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
      {contentToDisplay}
      <div style={{ flex: 1 }}></div> 

      {selectedPdfUrl && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          backgroundColor: 'transparent',
          zIndex: 9999, /* High z-index to overlay everything */
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
           <div style={{ 
             position: 'relative', 
             width: '90%', 
             height: '90%', 
             background: 'white', 
             borderRadius: '8px',
             display: 'flex',
             flexDirection: 'column'
           }}>
             <button
               onClick={closePdfPopup}
               className="pdf-close-button"
               style={{
                  // Default styles for desktop are now in CSS or fallback here
                  position: 'absolute',
                  top: '-15px',
                  right: '-15px',
                  width: '30px',
                  height: '30px',
                  background: 'red',
                  color: 'white',
                  border: '2px solid white',
                  borderRadius: '50%',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  zIndex: 10000,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
               }}
             >
               ✕
             </button>
             <iframe
               src={selectedPdfUrl}
               title="PDF Viewer"
               width="100%"
               height="100%"
               style={{ border: 'none', borderRadius: '8px' }}
             />
           </div>
        </div>
      )}
    </main>
  );
}

export default Content;


